import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { useDesignInit } from '@/hooks/useDesignInit'
import { useTheme } from '@/contexts/ThemeContext'
import type { DesignConfig } from '@/types/design'
import { BootSkeleton } from '@/components/ui/BootSkeleton'

interface DesignContextValue {
  design: DesignConfig
  logoUrl: string
  error: string | null
}

const DesignContext = createContext<DesignContextValue | null>(null)

export function DesignProvider({ children }: { children: ReactNode }) {
  const { booting, design, error } = useDesignInit()
  const { isDark } = useTheme()

  const value = useMemo<DesignContextValue>(
    () => ({
      design,
      logoUrl: isDark ? design.logos.dark_logo : design.logos.light_logo,
      error,
    }),
    [design, error, isDark],
  )

  if (booting) {
    return <BootSkeleton />
  }

  return (
    <DesignContext.Provider value={value}>{children}</DesignContext.Provider>
  )
}

export function useDesign(): DesignContextValue {
  const ctx = useContext(DesignContext)
  if (!ctx) {
    throw new Error('useDesign must be used within DesignProvider')
  }
  return ctx
}
